import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-72684f48"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "d-flex align-center ga-2" }
const _hoisted_2 = { key: 0 }

import {
  AssetManagementPlanMaster,
  DateObject,
  FunctionalLocation,
} from "@/classes";
import DateField from "@/components/base/inputs/DateField.vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  BarElement,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { computed, onMounted, ref, watch } from "vue";
import { Bar } from "vue-chartjs";
import NuxtAxiosInstance from "@/plugins/axios";
import Decimal from "decimal.js";
import FLOCSearch from "../asset-management-plan/FLOCSearch.vue";
import { useListStore } from "@/store/lists";

type Aggregate = (typeof aggregates)[number];

type GroupBy = (typeof groupBys)[number];

type PM02PM03Metrics = {
  pm02: Record<string, Decimal>;
  pm03: Record<string, Decimal>;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'PM02PM03Chart',
  props: {
    hideTitle: { type: Boolean },
    chartHeight: { default: "600px" },
    flocIds: {}
  },
  setup(__props: any) {

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const props = __props;

const $axios = NuxtAxiosInstance;
const lists = useListStore();

const ampMasters = lists.useList<AssetManagementPlanMaster>(
  "assetManagementPlanMasters",
);

const aggregates = ["Count", "Cost"] as const;
const groupBys = ["Month", "Year"] as const;
const start = ref<DateObject | null>(
  DateObject.add(new DateObject(), { years: -1 }),
);
const end = ref<DateObject | null>(new DateObject());
const ampMasterIds = ref<string[]>([]);
const selectedFlocs = ref<FunctionalLocation[]>([]);
const aggregate = ref<Aggregate>("Count");
const groupBy = ref<GroupBy>("Year");

const abortController = ref(new AbortController());
const fetchingCount = ref(0);
const metrics = ref<PM02PM03Metrics>({
  pm02: {},
  pm03: {},
});

onMounted(() => getData());

watch(
  () => props.flocIds,
  () => getData(),
);

const getData = async () => {
  try {
    abortController.value.abort();
    abortController.value = new AbortController();
    fetchingCount.value++;
    metrics.value = await $axios.$post<PM02PM03Metrics>(
      "/metrics/pm02-pm03",
      {
        start: start.value,
        end: end.value,
        aggregate: aggregate.value,
        amp_master_ids: ampMasterIds.value,
        floc_ids: props.flocIds ?? selectedFlocs.value.map((f) => f.id),
        group_by: groupBy.value,
      },
      {
        signal: abortController.value.signal,
      },
    );
  } catch (e) {
    // Aborted
  } finally {
    fetchingCount.value--;
  }
};

const data = computed<ChartData<"bar">>(() => {
  const keySet = new Set<string>();
  Object.keys(metrics.value.pm02).forEach((key) => {
    keySet.add(key);
  });
  Object.keys(metrics.value.pm03).forEach((key) => {
    keySet.add(key);
  });

  const keys = Array.from(keySet).sort();
  const pm02Values: number[] = [];
  const pm03Values: number[] = [];
  for (const key of keys) {
    const pm02 = metrics.value.pm02[key] ?? new Decimal(0);
    const pm03 = metrics.value.pm03[key] ?? new Decimal(0);
    pm02Values.push(pm02.toNumber());
    pm03Values.push(pm03.toNumber());
  }

  return {
    labels: keys,
    datasets: [
      {
        label: "PM02",
        data: pm02Values,
        borderColor: "rgb(75, 192, 192)",
        borderWidth: 3,
        fill: true,
        backgroundColor: "rgb(75, 192, 192, 0.2)",
        tension: 0,
      },
      {
        label: "PM03",
        data: pm03Values,
        borderColor: "rgb(245, 142, 47)",
        borderWidth: 3,
        fill: true,
        backgroundColor: "rgb(245, 142, 47, 0.2)",
        tension: 0,
      },
    ],
  };
});

const options = computed<ChartOptions<"bar">>(() => {
  const prefix = aggregate.value === "Cost" ? "$" : "";
  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        min: 0,
        stacked: true,
        ticks: {
          callback: (value) => {
            return `${prefix}${value.toLocaleString()}`;
          },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (item) => {
            return `${
              item.dataset.label
            }: ${prefix}${item.parsed.y.toLocaleString()}`;
          },
        },
      },
    },
  };
});

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_radio = _resolveComponent("v-radio")!
  const _component_v_radio_group = _resolveComponent("v-radio-group")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      (!_ctx.hideTitle)
        ? (_openBlock(), _createElementBlock("h3", _hoisted_2, "PM02/PM03"))
        : _createCommentVNode("", true),
      _createVNode(DateField, {
        class: "width-160",
        modelValue: start.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((start).value = $event)),
        label: "From",
        variant: "outlined",
        density: "compact",
        "hide-details": "",
        onChange: getData
      }, null, 8, ["modelValue"]),
      _createVNode(DateField, {
        class: "width-160",
        modelValue: end.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((end).value = $event)),
        label: "To",
        variant: "outlined",
        density: "compact",
        "hide-details": "",
        onChange: getData
      }, null, 8, ["modelValue"]),
      (_ctx.flocIds === undefined)
        ? (_openBlock(), _createBlock(_component_v_autocomplete, {
            key: 1,
            class: "width-160 compact-autocomplete",
            modelValue: ampMasterIds.value,
            "onUpdate:modelValue": [
              _cache[2] || (_cache[2] = ($event: any) => ((ampMasterIds).value = $event)),
              getData
            ],
            label: "AMP",
            items: _unref(ampMasters),
            "item-value": "id",
            "item-title": "name",
            multiple: "",
            variant: "outlined",
            density: "compact",
            width: "400px",
            "max-width": "400px",
            "hide-details": ""
          }, null, 8, ["modelValue", "items"]))
        : _createCommentVNode("", true),
      (_ctx.flocIds === undefined)
        ? (_openBlock(), _createBlock(FLOCSearch, {
            key: 2,
            modelValue: selectedFlocs.value,
            "onUpdate:modelValue": [
              _cache[3] || (_cache[3] = ($event: any) => ((selectedFlocs).value = $event)),
              getData
            ],
            label: "FLOC",
            variant: "outlined",
            density: "compact",
            width: "400px",
            "max-width": "400px",
            "hide-details": "",
            multiple: ""
          }, null, 8, ["modelValue"]))
        : _createCommentVNode("", true),
      _createVNode(_component_v_select, {
        class: "width-160",
        modelValue: groupBy.value,
        "onUpdate:modelValue": [
          _cache[4] || (_cache[4] = ($event: any) => ((groupBy).value = $event)),
          getData
        ],
        label: "Group By",
        items: groupBys,
        variant: "outlined",
        density: "compact",
        "hide-details": ""
      }, null, 8, ["modelValue"]),
      _createVNode(_component_v_radio_group, {
        modelValue: aggregate.value,
        "onUpdate:modelValue": [
          _cache[5] || (_cache[5] = ($event: any) => ((aggregate).value = $event)),
          getData
        ],
        inline: "",
        "hide-details": ""
      }, {
        default: _withCtx(() => [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(aggregates, (aggregate) => {
            return _createVNode(_component_v_radio, {
              key: aggregate,
              label: aggregate,
              value: aggregate
            }, null, 8, ["label", "value"])
          }), 64))
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_spacer),
      (fetchingCount.value > 0)
        ? (_openBlock(), _createBlock(_component_v_progress_circular, {
            key: 3,
            indeterminate: "",
            size: 25
          }))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", {
      class: "mt-2",
      style: _normalizeStyle({ height: _ctx.chartHeight })
    }, [
      _createVNode(_unref(Bar), {
        data: data.value,
        options: options.value
      }, null, 8, ["data", "options"])
    ], 4)
  ]))
}
}

})